<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    @submit.prevent="submit"
    @keypress.enter="submit"
  >
    <p class="text-justify mb-5">
      Esqueceu sua senha? Fique tranquilo que a gente vai te ajudar! Informe o documento cadastrado no campo abaixo para
      que a gente possa enviar todas as instruções de como recuperar a sua senha.
    </p>

    <v-row justify="center">
      <v-col cols="8" sm="4">
        <v-select
          v-model="documentType"
          :items="documentTypeItems"
          variant="outlined"
          density="compact"
          hide-details
          class="mb-4"
          @update:model-value="(type: DocumentType) => emit('update:modelValue', { ...modelValue, document: '', document_type: type } as User)"
        />
      </v-col>
    </v-row>

    <t-document-input v-model="user.document" :document-type="documentType" required />

    <v-row>
      <v-col cols="6">
        <t-btn
          block
          color="secondary"
          type="button"
          class="heading--text"
          large
          :loading="loading"
          :disabled="!valid"
          @click="dialog.show('/login')"
        >
          Voltar
        </t-btn>
      </v-col>
      <v-col cols="6">
        <t-btn
          block
          color="success"
          type="submit"
          class="heading--text"
          large
          :loading="loading"
          :disabled="!valid"
        >
          Avançar
        </t-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef, type Ref } from "vue";
import type User from "@/types/User";
import type { DocumentType } from "@/types/User";
import TBtn from "@/components/core/TBtn.vue";
import getPasswordRecoveryMethods from "@/api/requests/getPasswordRecoveryMethods";
import axios, { type AxiosError } from "axios";
import type ErrorResponse from "@/types/ErrorResponse";
import type RecoveryMethod from "@/types/RecoveryMethod";
import TDocumentInput from "../core/TDocumentInput.vue";
import { useDialog } from "@/stores/dialog";
import { useSnackbar } from "@/stores/snackbar";

interface Props {
  modelValue?: User;
}

const {
  modelValue = {} as User,
} = defineProps<Props>();

const emit = defineEmits<{
  "submit": [methods: RecoveryMethod[]];
  "update:modelValue": [user: User];
}>();

defineOptions({
  name: "FormGetRecoverCode",
});

const dialog = useDialog();
const snackbar = useSnackbar();
const form = useTemplateRef<{ validate: () => boolean }>("form");

const documentTypeItems = [
  { title: "CPF", value: "cpf" },
  { title: "Passaporte", value: "passport" },
];

const valid: Ref<boolean> = ref(true);
const loading: Ref<boolean> = ref(false);
const documentType: Ref<DocumentType> = ref("cpf");

const user = computed<User>(() => modelValue);

async function submit(): Promise<void> {
  try {
    if (form.value?.validate()) {
      loading.value = true;
      const methods = await getPasswordRecoveryMethods(user.value.document);
      emit("submit", methods);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const error = e as AxiosError<ErrorResponse>;

      if (error.response?.data.message) {
        snackbar.show(error.response?.data.message, "error");
      } else {
        snackbar.show(error.message, "error");
      }
    }
  } finally {
    loading.value = false;
  }
}
</script>
